import { COLORS } from '../../constants/theme';
import type { ActiveWarDto } from '../../types/diplomacy';

interface Props {
  activeWar: ActiveWarDto;
}

interface BenefitRow {
  label: string;
  value: string;
  type: 'bonus' | 'penalty' | 'neutral';
}

function getBenefitRows(war: ActiveWarDto): BenefitRow[] {
  const rows: BenefitRow[] = [];

  // Hostile benefits (always active for war opponent)
  rows.push({ label: 'Combat gains', value: '+10%', type: 'bonus' });
  rows.push({ label: 'Conquest NW range', value: '0.75× – 1.33× NW', type: 'bonus' });
  rows.push({ label: 'Mana cost (offensive)', value: '2%', type: 'bonus' });
  rows.push({ label: 'Stealth cost (offensive)', value: '2%', type: 'bonus' });

  // War immediate (phase 0+)
  if (!war.inCeasefire) {
    const attackTimeValue = war.warPhase >= 12 ? '−20%' : '−10%';
    rows.push({ label: 'Attack time', value: attackTimeValue, type: 'bonus' });
    rows.push({ label: 'War land bonus', value: '+10% from explore pool', type: 'bonus' });
    rows.push({ label: 'Enhanced Massacre', value: 'Destroys buildings', type: 'bonus' });
    rows.push({ label: 'Explore cost', value: '×2', type: 'penalty' });
    rows.push({ label: 'Paradise spell', value: 'Blocked', type: 'penalty' });
    rows.push({ label: 'Science rate', value: 'Capped at Active', type: 'penalty' });
    rows.push({ label: 'Stance effects', value: 'Negated', type: 'penalty' });
  }

  // Phase 24 modifiers
  if (war.warPhase >= 24 && !war.inCeasefire) {
    rows.push({ label: 'Military losses', value: '−25%', type: 'bonus' });
    rows.push({ label: 'Training time', value: '−25%', type: 'bonus' });
    rows.push({ label: 'Distance penalties', value: 'Removed', type: 'bonus' });
    rows.push({ label: 'Ops/spells vs others', value: '−75% (bidirectional)', type: 'penalty' });
  }

  // Ceasefire bonuses
  if (war.inCeasefire) {
    rows.push({ label: 'Birth rate', value: '+500%', type: 'bonus' });
    rows.push({ label: 'Training cost', value: '−30%', type: 'bonus' });
    rows.push({ label: 'Draft cost', value: '−50%', type: 'bonus' });
  }

  return rows;
}

function phaseLabel(phase: number): string {
  if (phase >= 24) return 'Phase 3 (24+ days)';
  if (phase >= 12) return 'Phase 2 (12+ days)';
  return 'Phase 1 (0+ days)';
}

export function WarBenefitsPanel({ activeWar }: Props) {
  const rows = getBenefitRows(activeWar);
  const typeColors: Record<string, string> = {
    bonus: COLORS.positive,
    penalty: '#c45a4a',
    neutral: COLORS.text.secondary,
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        padding: 12,
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          War Modifiers
        </span>
        <span
          style={{
            marginLeft: 8,
            fontSize: 11,
            color: activeWar.inCeasefire ? '#6a9e8e' : '#c45a4a',
            fontWeight: 600,
          }}
        >
          {activeWar.inCeasefire ? 'Ceasefire' : phaseLabel(activeWar.warPhase)}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((row) => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: COLORS.text.secondary }}>{row.label}</span>
            <span style={{ color: typeColors[row.type], fontWeight: 600, marginLeft: 8 }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
